import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  provideRouter,
  RouterFeatures,
  TitleStrategy,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import {
  DATA_CONFIG,
  DataConfig,
  GOOGLE_MAPS_EMBED_SRC_STUDIO_URL,
} from './injection-tokens';
import { RouteTitleStrategy } from './page-title';
import { SEOService } from './seo.service';

export interface CustomProvidersOptions {
  /**
   * Resolve the initial navigation BEFORE bootstrap completes.
   *
   * The real app (browser bootstrap + SSR/prerender) sets this. Without it the
   * router snapshot is empty when `RouteUiDataService` is constructed, so shell
   * flags seeded from route data (`stageExtension` → `.shell-body--bleed`) start
   * false. Bootstrap runs before `DOMContentLoaded`, strips the class the SSR
   * HTML already painted, and `NavigationEnd` re-adds it — the shell reflows
   * 1350 → 1280 → 1350px, two equal layout shifts. Measured CLS on the
   * production build at desktop 1350×940: `/x-ploration-jazz` 0.197 → 0.005 and
   * home 0.084 → 0.000 (the 0.09 budget was one nudge away on home already).
   *
   * Component tests leave it OFF: TestBed renders a component directly with no
   * URL, so blocking on an initial navigation resolves the `''` route and
   * double-renders the shell under the component under test.
   */
  blockingInitialNavigation?: boolean;
}

export function provideCustomProviders(
  options: CustomProvidersOptions = {},
): EnvironmentProviders {
  const routerFeatures: RouterFeatures[] = [
    withComponentInputBinding(),
    withInMemoryScrolling({
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
    }),
    // Pin the pre-v22 default explicitly: child routes (about/, bass-lessons/
    // use `children`) receive ONLY their own params as component inputs, not
    // the parent's. Angular 22 flipped this default toward `always`; with
    // withComponentInputBinding() that could silently bind parent params/data
    // onto child inputs. Keep intent durable + behavior unchanged.
    withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' }),
  ];

  if (options.blockingInitialNavigation) {
    routerFeatures.push(withEnabledBlockingInitialNavigation());
  }

  return makeEnvironmentProviders([
    provideZonelessChangeDetection(),
    provideRouter(routes, ...routerFeatures),
    // provideHttpClient() removed 2026-09-14: it was wired for a Vercel
    // image loader (withFetch()) that fe91f66d dropped, and nothing in
    // src/app or @groove/angular's dist injects HttpClient — verified via
    // `rg -l HttpClient src/app` (customer-providers.ts itself was the only
    // hit, from this provider registration) and the same grep against
    // node_modules/@groove/angular (no hits). Every network-ish surface in
    // this app (Google Maps embed, YouTube iframes) is a plain iframe/anchor
    // src, not an HttpClient call.
    provideAppInitializer(() => {
      inject(SEOService);
    }),
    { provide: TitleStrategy, useClass: RouteTitleStrategy },
    {
      provide: DATA_CONFIG,
      useValue: {
        email: 'russrodgers@gmail.com',
        phone: '404-386-0983',
        // Derived hrefs live here so every call/email CTA shares one
        // source of truth instead of recomputing `tel:`/`mailto:` per
        // component. The phone is already in display form ("404-386-0983").
        emailHref: 'mailto:russrodgers@gmail.com',
        phoneHref: 'tel:404-386-0983',
        paypalLink: 'https://www.paypal.com/us/webapps/mpp/account-selection',
        paypalUsername: 'russrodgers@gmail.com',
        venmoLink: 'https://venmo.com/',
        venmoUsername: 'Russell Rodgers (@Russell-Rodgers-2)',
        cashAppLink: 'https://cash.app/',
        cashAppUsername: '$russbass',
        zelleLink: 'https://www.zellepay.com/',
        zelleUsername: 'russrodgers@gmail.com',
        facebook: {
          link: 'https://www.facebook.com/russrodgersbassguitar/',
          displayText: 'Facebook',
        },
        instagram: {
          link: 'https://www.instagram.com/russbassrodgers/',
          displayText: 'Instagram',
        },
        youtube: {
          link: 'https://www.youtube.com/@RussellRodgers/videos',
          displayText: 'YouTube',
        },
        linkedin: {
          link: 'https://www.linkedin.com/in/russ-rodgers-3724904/',
          displayText: 'LinkedIn',
        },
        youtubeLessonsPlaylist:
          'https://www.youtube.com/watch?v=yATeWCbCgoc&list=PLnRW_LWLpwncZChO8sz0QgARU2mbuy3k8&index=1',
      } satisfies DataConfig,
    },
    {
      provide: GOOGLE_MAPS_EMBED_SRC_STUDIO_URL,
      deps: [DomSanitizer],
      useFactory: (sanitizer: DomSanitizer): SafeResourceUrl =>
        sanitizer.bypassSecurityTrustResourceUrl(
          `https://www.google.com/maps/embed/v1/place?key=${environment.googleMapsApiKey}&q=Russ+Rodgers+Bass+Guitar+Instruction`,
        ),
    },
  ]);
}
